import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'
import { diggingAjax } from '../ajax/DiggingAjax'
import { store } from '../state/store'
import type { ActionResponse } from '../DTOs/Common/ErrorResponse'
import { DiggingLocationDTO } from '../DTOs/Production/DiggingLocationDTO'
import { appendCachedDiggingSummaryRow } from './patchCachedDiggingSummary'
import { computeShiftId } from './computeShiftId'
import { enqueue } from './queueOps'
import { nextTempId } from './tempId'
import type { DoubleShift } from '../types/Shift'
import { isOnlineNow } from './connectivity'

function currentShiftId(): number {
  const { selectedDate, selectedShift } = store.getState().shift
  return computeShiftId(selectedDate, selectedShift)
}

// Note: a digging pattern's StartTime is a TimeOnly server-side (the online path sends it through
// formatDatesForTimeOnlyModel), so the queued payload has to be "HH:mm:ss" too. Sending a full ISO
// timestamp failed to bind and the mutation came back as an error at sync.
function timeOnly(startTime: DateTime): string {
  return startTime.toFormat('HH:mm:ss')
}

export const diggingPatternMutations = {
  // Note: shiftIdOverride is for the provisional next shift created by an offline completion - the
  // pattern belongs to that shift, not to the one still selected in the breadcrumbs.
  async insertDiggingLocation(
    shiftDate: DateTime,
    dayOrNight: DoubleShift,
    diggerId: number,
    location: DiggingLocationDTO,
    shiftIdOverride?: number,
  ) {
    if (isOnlineNow()) return diggingAjax.insertDiggingLocation(shiftDate, dayOrNight, diggerId, location)
    const tempId = uuidv4()
    // Note: the negative temp id is stored as entityId so it is sent to the server as the
    // pattern's DiggingPatternId. The server maps it to the real id and repoints any loads
    // queued against it. The same id is returned here so the optimistic location (and the
    // loads added to it) reference the value the server will remap.
    const tempDiggingPatternId = await nextTempId()
    await enqueue({
      entity: 'diggingPattern',
      operation: 'create',
      shiftId: shiftIdOverride ?? currentShiftId(),
      tempId,
      entityId: tempDiggingPatternId,
      data: {
        diggerId,
        patternBlastId: location.patternBlastId,
        startTime: timeOnly(location.startTime),
        blastMasterId: location.blastMasterId,
      },
    })
    const created = new DiggingLocationDTO({ ...location, diggingPatternId: tempDiggingPatternId })
    appendCachedDiggingSummaryRow(created, shiftDate, dayOrNight, diggerId)

    return { success: true, data: created as DiggingLocationDTO | null }
  },

  async updateDiggingLocation(location: DiggingLocationDTO) {
    if (isOnlineNow()) return diggingAjax.updateDiggingLocation(location)
    await enqueue({
      entity: 'diggingPattern',
      operation: 'update',
      shiftId: currentShiftId(),
      entityId: location.diggingPatternId,
      rowVersion: location.rowVersion,
      data: {
        patternBlastId: location.patternBlastId,
        startTime: timeOnly(location.startTime),
        blastMasterId: location.blastMasterId,
      },
    })
    return { success: true, data: location as DiggingLocationDTO | null }
  },

  async deleteDiggingPattern(diggingPatternId: number, rowVersion?: string) {
    if (isOnlineNow()) return diggingAjax.deleteDiggingPattern(diggingPatternId)
    await enqueue({
      entity: 'diggingPattern',
      operation: 'delete',
      shiftId: currentShiftId(),
      entityId: diggingPatternId,
      rowVersion,
    })
    return { success: true, data: null as null }
  },
}
